import React from "react";
import { useTranslation } from "react-i18next";
import {
	DEFAULT_LANGUAGE,
	LANGUAGE_STORAGE_KEY,
	SUPPORTED_LANGUAGES,
	SupportedLanguage,
} from "../../i18n";

interface LanguageSwitcherProps {
	className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ className = "" }) => {
	const { i18n, t } = useTranslation();

	const currentLanguage = SUPPORTED_LANGUAGES.includes(
		i18n.language as SupportedLanguage,
	)
		? (i18n.language as SupportedLanguage)
		: DEFAULT_LANGUAGE;

	const handleLanguageChange = (language: SupportedLanguage) => {
		if (language === currentLanguage) {
			return;
		}

		void i18n.changeLanguage(language);
		try {
			localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
		} catch {
			// Ignore storage errors
		}
		document.documentElement.lang = language;
	};

	return (
		<div
			className={`inline-flex items-center rounded-lg border border-gray-200 bg-white p-1 shadow-sm ${className}`}
			role="group"
			aria-label={t("language.label")}
		>
			{SUPPORTED_LANGUAGES.map((language) => {
				const isActive = currentLanguage === language;
				return (
					<button
						key={language}
						type="button"
						onClick={() => handleLanguageChange(language)}
						className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
							isActive
								? "bg-primary-500 text-white shadow-sm"
								: "text-gray-600 hover:text-primary-600 hover:bg-primary-50"
						}`}
						aria-pressed={isActive}
					>
						{language.toUpperCase()}
					</button>
				);
			})}
		</div>
	);
};

export default LanguageSwitcher;
